// Full-screen first-run wizard. Shown ONCE for brand-new accounts on first
// login. Walks the owner through the 5 highest-impact setup steps in order
// so they hit "first booked job" inside 5-10 minutes instead of bouncing
// off the 20-tab sidebar.
//
// Hides after dismissal (localStorage flag) OR after onboarding completion
// (server flips accounts.onboarding_completed=1 once all required steps done).
// The existing OnboardingWizard floating popup stays as the ongoing
// follow-along; this one is just the first-touch prominent walkthrough.

import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Spinner } from '../ui.jsx';

const STORAGE_KEY = 'tradeops:firstRunDismissed';

export function FirstRunWizard({ go }) {
  const [setup, setSetup] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });
  const [step, setStep] = useState(0); // 0 = welcome, 1..5 = step cards
  useEffect(() => {
    api('/api/settings/setup-checklist').then(setSetup).catch(()=>setSetup({ complete: true }));
  }, []);
  // Hide if already dismissed OR onboarding is complete OR the API didn't return
  if (dismissed) return null;
  if (!setup) return null;
  if (setup.complete) return null;
  // Skip the wizard if the user has already done 3+ steps — they're past the
  // "I'm new and confused" phase, the smaller follow-along widget is enough.
  if (setup.progress && setup.progress.done >= 3) return null;

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch {}
    setDismissed(true);
  };
  const goToStep = (gotoTab) => {
    dismiss();
    if (gotoTab) go(gotoTab);
  };

  const steps = (setup.steps || []).filter(s => !s.optional);
  const nextUndone = steps.find(s => !s.done);
  const progress = setup.progress || { done: 0, total: steps.length, pct: 0 };

  // Welcome card (step 0) introduces the platform + bridges to checklist
  const welcomeCard = (
    <div style={{ textAlign:'center' }}>
      <div style={{ fontSize:46, marginBottom:8 }}>👋</div>
      <h1 style={{ margin:'0 0 8px', fontSize:30 }}>Welcome to TradeOps</h1>
      <p style={{ margin:'0 auto 24px', color:G.textMuted, maxWidth:520, fontSize:15, lineHeight:1.55 }}>
        Five quick steps get you to your first AI-booked job. Most owners knock it out in under 10 minutes.
        We'll only ever ask for what's needed — skip any step and come back to it.
      </p>
      <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap' }}>
        <Btn v="primary" sz="lg" onClick={()=>setStep(1)}>Let's go</Btn>
        <Btn v="ghost" sz="lg" onClick={dismiss}>Skip — I'll explore on my own</Btn>
      </div>
    </div>
  );

  // Step card for each setup checklist item
  const stepCard = () => {
    const i = step - 1;
    const item = steps[i];
    if (!item) return welcomeCard;
    const stepLabels = {
      phone: { icon: '📞', oneLiner: "Provision your business phone number.", why: "Your Twilio number is what customers see when the AI texts or calls them. Takes 30 seconds." },
      ai_voice: { icon: '🤖', oneLiner: "Import the number into ElevenLabs so the AI can answer calls.", why: "One click — we wire your Twilio number into the EL conversational AI agent + flip on inbound + outbound calling." },
      inspection_fee: { icon: '💵', oneLiner: "Set your diagnostic / service fee.", why: "The AI quotes this before booking and waits for the homeowner to confirm. Default is $69, change to whatever you charge." },
      team: { icon: '👷', oneLiner: "Add at least one tech or dispatcher.", why: "Jobs auto-route to the best-matched tech based on skills + location + availability. You need at least one person on the team." },
      contacts: { icon: '📇', oneLiner: "Add or import your first 10 customers.", why: "The AI needs people to follow up with. Drop a CSV or add a few manually — past customers come first since they convert highest." },
      stripe: { icon: '💳', oneLiner: "Connect Stripe to get paid online.", why: "Customers can pay invoices with Apple Pay, Google Pay, Klarna, Afterpay, Affirm, card, or ACH — all auto-enabled once Stripe is connected." },
      calendar: { icon: '📅', oneLiner: "Connect Google or Outlook calendar.", why: "When the AI books a job, the appointment lands directly on your calendar. No double-booking." },
    };
    const meta = stepLabels[item.id] || { icon: '✅', oneLiner: item.label, why: item.help || '' };
    return (
      <div style={{ textAlign:'center' }}>
        <div style={{ fontSize:46, marginBottom:8 }}>{item.done ? '✅' : meta.icon}</div>
        <div style={{ fontSize:11, fontWeight:700, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.08em', marginBottom:6 }}>
          Step {i+1} of {steps.length}
        </div>
        <h2 style={{ margin:'0 0 8px', fontSize:24 }}>{item.label}</h2>
        <p style={{ margin:'0 auto 18px', color:G.textMuted, maxWidth:520, fontSize:14, lineHeight:1.6 }}>
          {meta.why}
        </p>
        {item.done ? (
          <div style={{ background:G.greenLight, color:G.green, padding:'10px 14px', borderRadius:8, fontSize:13, fontWeight:700, marginBottom:18, display:'inline-block' }}>
            Already done — nice.
          </div>
        ) : (
          <div style={{ marginBottom:18 }}>
            <Btn v="primary" sz="lg" onClick={()=>goToStep(item.goto)}>{`Open ${item.goto || 'setup'}`}</Btn>
          </div>
        )}
        <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap' }}>
          {step > 1 && <Btn v="ghost" onClick={()=>setStep(step-1)}>Back</Btn>}
          {step < steps.length && <Btn v="ghost" onClick={()=>setStep(step+1)}>Next step</Btn>}
          {step === steps.length && <Btn v="primary" onClick={dismiss}>Done — close walkthrough</Btn>}
          <Btn v="ghost" onClick={dismiss}>Skip for now</Btn>
        </div>
      </div>
    );
  };

  return <div style={{
    position:'fixed', inset:0, background:'rgba(0,0,0,.75)', backdropFilter:'blur(8px)',
    zIndex:9000, display:'flex', alignItems:'center', justifyContent:'center', padding:24,
  }}>
    <div style={{
      ...styles.card, padding:'40px 36px', maxWidth:680, width:'100%',
      background:G.card, position:'relative',
    }}>
      <button onClick={dismiss} aria-label="Close" style={{
        position:'absolute', top:14, right:14, background:'transparent', border:'none',
        fontSize:24, color:G.textMuted, cursor:'pointer', padding:6, lineHeight:1,
      }}>×</button>
      {step > 0 && (
        <div style={{ marginBottom:18 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', fontSize:11, color:G.textMuted, marginBottom:6 }}>
            <span>{progress.done} of {progress.total} done · {progress.pct}%</span>
          </div>
          <div style={{ height:4, width:'100%', background:G.input, borderRadius:2 }}>
            <div style={{ height:'100%', width:`${progress.pct}%`, background:G.purple, borderRadius:2, transition:'width .25s' }}/>
          </div>
        </div>
      )}
      {step === 0 ? welcomeCard : stepCard()}
    </div>
  </div>;
}
